import type {LicenseInfo} from '@github-ui/code-view-sidebar/types'
import {SidebarAbout} from '@github-ui/code-view-sidebar/components/SidebarAbout'
import {clsx} from 'clsx'
import {RepoHeaderActions} from '@github-ui/repo-header-actions'
import {useMemo} from 'react'
import {RepoSubtitle, RepoTitle} from './components/RepoTitle'
import type {HeaderResponse} from './types'
import {deriveRepoPaths} from './utils/repo-paths'
import {AdvisoryWorkspaceHeader} from './components/AdvisoryWorkspaceHeader'
import styles from './CodeViewHeader.module.css'
import {useErrorMessage} from './utils/use-error-message'

/** Data extracted from overviewFiles for the mobile about section. */
export interface OverviewFilesData {
  licenses?: LicenseInfo[]
  hasReadme?: boolean
  hasCodeOfConduct?: boolean
  hasContributing?: boolean
  hasSecurityPolicy?: boolean
  hasAccessibility?: boolean
}

/** Repository overview page header component for the code view. */
export function CodeViewHeader({
  ownerLogin,
  repoName,
  viewer,
  repo,
  advisoryInfo,
  showDemoNotification,
  hasAccessToCodespaces,
  licenses,
  hasReadme,
  hasCodeOfConduct,
  hasContributing,
  hasSecurityPolicy,
  hasAccessibility,
  ...rest
}: HeaderResponse & OverviewFilesData & {hasAccessToCodespaces?: boolean}) {
  const paths = useMemo(() => deriveRepoPaths(ownerLogin, repoName), [ownerLogin, repoName])
  const showError = useErrorMessage()
  if (!viewer) {
    return null
  }

  const title = (
    <RepoTitle
      repo={repo}
      ownerLogin={ownerLogin}
      repoName={repoName}
      paths={paths}
      isOrgOwned={rest.pin.isOrgOwned}
      showDemoNotification={showDemoNotification}
    />
  )
  const subtitle = <RepoSubtitle repo={repo} advisoryInfo={advisoryInfo} />
  const actions = !repo.isAdvisoryWorkspace && (
    <div>
      <RepoHeaderActions
        {...rest}
        ownerLogin={ownerLogin}
        repoName={repoName}
        viewer={viewer}
        repo={repo}
        paths={paths}
        hasAccessToCodespaces={hasAccessToCodespaces}
        onError={showError}
      />
    </div>
  )

  return (
    <>
      {repo.isAdvisoryWorkspace && <AdvisoryWorkspaceHeader repo={repo} advisoryInfo={advisoryInfo} />}
      <div className="tmp-pt-3 tmp-pb-3 border-bottom color-border-muted">
        <div className="d-flex flex-wrap flex-items-center gapx-3 gapy-1">
          <div className={styles.TitleWrapper}>
            {title}
            {subtitle}
          </div>
          {actions}
        </div>
      </div>
      {!repo.isAdvisoryWorkspace && (
        <div className={clsx(styles.NarrowOnly, 'border-bottom color-border-muted')}>
          <SidebarAbout
            {...rest}
            ownerLogin={ownerLogin}
            repoName={repoName}
            licenses={licenses}
            hasReadme={hasReadme ?? false}
            hasCodeOfConduct={hasCodeOfConduct}
            hasContributing={hasContributing}
            hasSecurityPolicy={hasSecurityPolicy}
            hasAccessibility={hasAccessibility}
          />
        </div>
      )}
    </>
  )
}
